import {
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useCombobox,
  useMultipleSelection,
} from 'downshift'
import { motion } from 'framer-motion'
import uniqueId from 'lodash/uniqueId'
import { ChangeEvent, Fragment, useCallback, useMemo, useState } from 'react'
import { usePaginatedUsers, useUserGroups } from '@opallabs/api'
import { UserLike } from '@opallabs/concepts'
import { Color } from '@opallabs/gem'
import {
  useDebouncedValue,
  useMultiSelectDiff,
  useWorkspace,
} from '@opallabs/hooks'
import {
  Avatar,
  TextInput,
  MenuOption,
  DotLoader,
  menuOptionSelectors,
} from '@opallabs/lapidary'
import { usersSelector, userGroupsSelector } from './selectors'
import * as styles from './styles'
import { SearchableUserListProps } from './types'
import { usePopoverAnimation } from './usePopoverAnimation'

const SEARCH_DEBOUNCE_MS = 200

export const SearchableUserList = <TUserLike extends UserLike>({
  label,
  excludedUsers,
  initialSelection = [],
  onMenuCloseDiff,
  onSelectionChange,
  placeholder = 'Search...',
  restrictToWorkspace = [],
  showUserGroups = false,
  sortSelectedToTop = true,
  width = '100%',
}: SearchableUserListProps<TUserLike>) => {
  const workspace = useWorkspace()

  const [searchValue, setSearchValue] = useState('')
  const [componentId] = useState(uniqueId('satellite-user-list-'))
  const { isMounted, opacity, scale, mountAnimation, unmountAnimation } =
    usePopoverAnimation()

  const trimmedSearchValue = searchValue.trim()
  const debouncedSearchValue = useDebouncedValue(
    trimmedSearchValue,
    SEARCH_DEBOUNCE_MS
  )

  const { users, fetchingComplete: usersFetched } = usePaginatedUsers({
    filter: {
      name: debouncedSearchValue || undefined,
      brand_id: restrictToWorkspace.includes('user')
        ? [parseInt(workspace.id, 10)]
        : undefined,
    },
    include: ['avatar_asset_reference'],
    select: useCallback(usersSelector, []),
  })
  const { data: userGroups = [], isFetched: groupsFetched } = useUserGroups({
    filter: {
      name: debouncedSearchValue || undefined,
      members: restrictToWorkspace.includes('user_group')
        ? { workspace_id: [workspace.uuid] }
        : undefined,
    },
    select: useCallback(userGroupsSelector, []),
    enabled: showUserGroups,
  })

  const fetchingComplete = usersFetched && (groupsFetched || !showUserGroups)
  const availableUsers = useMemo(
    () =>
      [...users, ...userGroups].sort((a, b) =>
        a.name.localeCompare(b.name)
      ) as TUserLike[],
    [users, userGroups]
  )

  // Track when menu opens to sort selected items to top
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [selectionForSort, setSelectionForSort] = useState<TUserLike[]>([])

  const { onSelect, selectedItems, getMultiSelectDiff } = useMultiSelectDiff({
    initialSelection,
    key: 'uuid',
  })
  const { getDropdownProps } = useMultipleSelection({
    itemToKey: (item) => item?.uuid,
    initialSelectedItems: initialSelection,
    selectedItems,
  })
  const selectedUuids = useMemo(
    () => selectedItems.map((item) => item.uuid),
    [selectedItems]
  )

  const sortedItems = useMemo(() => {
    // Sort selected items to top if enabled and menu is open
    if (sortSelectedToTop && internalIsOpen) {
      const isExcluded = (item: UserLike) =>
        excludedUsers !== undefined &&
        excludedUsers.findIndex((excluded) => excluded.uuid === item.uuid) !==
          -1

      const filteredUsers = availableUsers.filter((user) =>
        excludedUsers?.length ? !isExcluded(user) : true
      )

      const selectedUuids = selectionForSort.map((item) => item.uuid)
      const selectedUsers = filteredUsers
        .filter((user) => selectedUuids.includes(user.uuid))
        .sort((a, b) => a.name.localeCompare(b.name))
      const unselectedUsers = filteredUsers.filter(
        (user) => !selectedUuids.includes(user.uuid)
      )

      return [...selectedUsers, ...unselectedUsers]
    }

    // Default behavior: just filter excluded users
    return availableUsers.filter((user) =>
      excludedUsers?.length
        ? excludedUsers.findIndex((excluded) => excluded.uuid === user.uuid) ===
          -1
        : true
    )
  }, [
    availableUsers,
    sortSelectedToTop,
    internalIsOpen,
    selectionForSort,
    excludedUsers,
  ])

  const stateReducer = (
    state: UseComboboxState<TUserLike>,
    actionAndChanges: UseComboboxStateChangeOptions<TUserLike>
  ): Partial<UseComboboxState<TUserLike>> => {
    const { changes, type } = actionAndChanges
    switch (type) {
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.ItemClick:
        return {
          ...changes,
          // Keep menu open after selection
          isOpen: true,
          highlightedIndex: state.highlightedIndex,
        }
      case useCombobox.stateChangeTypes.InputBlur:
        return {
          ...changes,
          // When blurred by keyboard, Downshift will try to select the
          // highlighted item. Ignore it if the item is already selected
          selectedItem:
            changes.selectedItem &&
            selectedUuids.includes(changes.selectedItem.uuid)
              ? state.selectedItem
              : changes.selectedItem,
        }
    }

    return changes
  }

  const onStateChange = ({
    inputValue: newInputValue,
    selectedItem: newSelectedItem,
    type,
  }: UseComboboxStateChange<TUserLike>) => {
    switch (type) {
      case useCombobox.stateChangeTypes.InputChange:
        setSearchValue(newInputValue ?? '')
        break
      // Selection on blur is undesirable for a multi-select where
      // selected options remain in the menu, so we won't trigger
      // on InputBlur
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.ItemClick:
        if (newSelectedItem) {
          onSelectedItemChange({ type, selectedItem: newSelectedItem })
        }
    }
  }

  const onIsOpenChange = ({ isOpen }: UseComboboxStateChange<TUserLike>) => {
    if (isOpen) {
      mountAnimation()
      setInternalIsOpen(true)
      setSelectionForSort(selectedItems)
    } else {
      unmountAnimation()
      setInternalIsOpen(false)
      onMenuCloseDiff?.(getMultiSelectDiff())
      if (searchValue !== '') {
        setSearchValue('')
      }
    }
  }

  const onSelectedItemChange = ({
    selectedItem,
  }: UseComboboxStateChange<TUserLike>) => {
    if (!selectedItem) {
      return
    }

    const verb = onSelect(selectedItem)
    onSelectionChange?.(selectedItem, verb)
  }

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox({
    id: componentId,
    items: sortedItems,
    itemToString: (item) => item?.name ?? '',
    inputValue: searchValue,
    selectedItem: null,
    onStateChange,
    onIsOpenChange,
    stateReducer,
  })

  const {
    'aria-controls': controlsHtmlId,
    autoComplete: autocompleteHint,
    id: inputId,
    ref: inputRef,
    onKeyDown,
    onBlur,
    onChange: onInputChange,
    onChangeText, // eslint-disable-line @typescript-eslint/no-unused-vars
    onClick,
    onInput, // eslint-disable-line @typescript-eslint/no-unused-vars
    value: inputValue,
    ...ariaLabels
  } = getInputProps(
    getDropdownProps({ preventKeyAction: isOpen, 'aria-label': label })
  )

  return (
    <div
      css={styles.container}
      className="satellite-searchable-user-list"
      style={{ inlineSize: width }}
    >
      <TextInput
        ref={inputRef}
        additionalAriaLabels={ariaLabels}
        autocompleteHint={autocompleteHint}
        onChange={setSearchValue}
        htmlId={inputId}
        controlsHtmlId={controlsHtmlId}
        placeholder={placeholder}
        onInputBlur={onBlur}
        // TextInput's onChange passes only the value, but downshift needs the event
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          onInputChange?.(event)
        }
        onClick={onClick}
        onKeyDown={onKeyDown}
        trailingIconGlyph={inputValue ? undefined : 'gem-search'}
        clearable={!!inputValue}
        value={inputValue}
      />
      <motion.ul
        {...getMenuProps()}
        css={styles.list}
        style={{
          scale: scale,
          opacity: opacity,
          transformOrigin: 'top',
        }}
      >
        {isMounted && (
          <Fragment>
            {sortedItems.map((user, index) => {
              const selected = selectedUuids.includes(user.uuid)
              const { id, ...itemProps } = getItemProps({
                item: user,
                index,
                'aria-selected': selected,
              })
              return (
                <MenuOption.Root
                  key={`${user.type}:${user.uuid}`}
                  id={id}
                  optionText={user.name}
                  selected={selected}
                >
                  <MenuOption.Body
                    css={styles.menuOption}
                    highlightedByKeyboard={highlightedIndex === index}
                    {...itemProps}
                  >
                    <MenuOption.Checkbox />
                    <Avatar
                      size={24}
                      fullName={user.name}
                      src={user.asset?.url}
                      css={
                        user.type === 'user_group'
                          ? {
                              backgroundColor:
                                Color.dark['Color/Surface/Surface-4'],
                              color: Color.dark['Text/Primary'],
                            }
                          : undefined
                      }
                    />
                    <MenuOption.OptionText css={styles.menuOptionText}>
                      {user.type === 'user_group' && (
                        <span
                          className={menuOptionSelectors.menuOptionTrailingText}
                        >
                          {user.memberCount ?? user.members.length.toString()}
                        </span>
                      )}
                    </MenuOption.OptionText>
                    {initialSelection.find((u) => {
                      if (u.type === 'user_group') {
                        return (
                          u.uuid === user.uuid ||
                          u.members.find((m) => m.uuid === user.uuid)
                        )
                      }
                      return u.uuid === user.uuid
                    }) && (
                      <span
                        className={menuOptionSelectors.menuOptionTrailingText}
                      >
                        Can Access
                      </span>
                    )}
                  </MenuOption.Body>
                </MenuOption.Root>
              )
            })}
            {!fetchingComplete && (
              <li css={styles.loaderListItem}>
                <DotLoader />
              </li>
            )}
            {!sortedItems.length && fetchingComplete && (
              <li css={styles.emptySearch}>No users match your search</li>
            )}
          </Fragment>
        )}
      </motion.ul>
    </div>
  )
}
